import Alert from 'common/components/alert/Alert';
import FinalFormField from 'common/components/final-form/FinalFormField';
import Modal from 'common/components/modal/Modal';
import formatAddress from 'common/services/address/format-address';
import AlertType from 'common/types/AlertType';
import ChsAddress from 'domain/types/ChsAddress';
import ChsProfile from 'domain/types/ChsProfile';
import {
  Button,
  Col,
  FormControl,
  FormGroup,
  FormLabel,
  Row,
} from 'react-bootstrap';
import { Field, Form } from 'react-final-form';

interface ICancelRegistrationPresenter {
  profile: ChsProfile;
  registrationAddresses: Array<ChsAddress> | null;
  onCancelFormSubmit: (values: Record<string, unknown>) => Promise<void>;
  isModalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  cancelFormSubmitLoading: boolean;
  cancelFormSubmitErrors: string[] | null;
}

function CancelRegistrationPresenter(props: ICancelRegistrationPresenter) {
  const sortedAddresses = props.registrationAddresses?.sort((a, b) => {
    const aStreetName = a.streetName?.toLowerCase() ?? '';
    const bStreetName = b.streetName?.toLowerCase() ?? '';
    if (aStreetName < bStreetName) {
      return -1;
    }
    if (aStreetName > bStreetName) {
      return 1;
    }

    return 0;
  });
  const required = (value: any) =>
    value ? undefined : 'This field is required.';

  const errorAlert = props.cancelFormSubmitErrors && (
    <Alert type={AlertType.danger}>
      <ul>
        {props.cancelFormSubmitErrors.map((e) => {
          if (e) {
            return <li key={e}>{e}</li>;
          }
        })}
      </ul>
    </Alert>
  );

  return (
    <>
      <h1 className='mb-3'>Request to Cancel Your Registration</h1>
      <Row>
        <Col md='8'>
          <p>
            If you wish to cancel your registration and remove any of your
            listings from their respective shared housing platforms, you must
            first submit your information below to start this process.
          </p>
        </Col>
      </Row>
      <Row>
        <Col md='6'>
          {errorAlert}
          <Form
            onSubmit={props.onCancelFormSubmit}
            initialValues={{
              firstName: props.profile.firstName,
              lastName: props.profile.lastName,
              email: props.profile.email,
              address: '',
              reason: '',
            }}
          >
            {({ handleSubmit, form }) => (
              <form
                onSubmit={(event) => {
                  handleSubmit(event)?.then(() => {
                    props.setModalOpen(true);
                    form.reset();
                    form.resetFieldState('address');
                    form.resetFieldState('reason');
                  });
                }}
              >
                <FinalFormField
                  name='firstName'
                  label='First Name *'
                  disabled
                  validate={required}
                />
                <FinalFormField
                  name='lastName'
                  label='Last Name *'
                  disabled
                  validate={required}
                />
                <FinalFormField
                  name='email'
                  label='Email *'
                  disabled
                  validate={required}
                />
                <Field name='address' validate={required}>
                  {({ input, meta }) => (
                    <FormGroup>
                      <FormLabel>Address *</FormLabel>
                      <FormControl {...input} as='select'>
                        <option value=''>--</option>
                        {sortedAddresses?.map((a, idx) => (
                          <option value={formatAddress(a)} key={idx}>
                            {formatAddress(a)}
                          </option>
                        ))}
                      </FormControl>
                      {meta.error && meta.touched && (
                        <FormControl.Feedback
                          type='invalid'
                          className='d-block'
                        >
                          {meta.error}
                        </FormControl.Feedback>
                      )}
                    </FormGroup>
                  )}
                </Field>
                <Field name='reason' type='textarea' validate={required}>
                  {({ input, meta }) => (
                    <FormGroup>
                      <FormLabel>Reason *</FormLabel>
                      <FormControl {...input} as='textarea' />
                      {meta.error && meta.touched && (
                        <FormControl.Feedback
                          type='invalid'
                          className='d-block'
                        >
                          {meta.error}
                        </FormControl.Feedback>
                      )}
                    </FormGroup>
                  )}
                </Field>
                <Button
                  type='submit'
                  variant='primary'
                  disabled={props.cancelFormSubmitLoading}
                >
                  Submit
                </Button>
              </form>
            )}
          </Form>
        </Col>
      </Row>
      <Modal
        show={props.isModalOpen}
        toggle={() => props.setModalOpen((prev) => !prev)}
        title='Success'
        body={
          <>
            <p>
              Form successfully submitted. A confirmation email has been sent
              for your records.
            </p>
            <div className='d-flex'>
              <Button
                type='button'
                variant='primary'
                className='ml-auto'
                onClick={() => props.setModalOpen(false)}
              >
                Close
              </Button>
            </div>
          </>
        }
      />
    </>
  );
}

export default CancelRegistrationPresenter;
